import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormState, UseFormReset, UseFormWatch } from 'react-hook-form';

import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { Question } from '@/models/Question';

type BaseFields = Record<string, unknown>;

const isUnsavedDraft = <T extends BaseFields>(
  values: T,
  previousDefaults: T,
  savedValues?: T,
) => !isEqual(values, previousDefaults) && !isEqual(values, savedValues);

type UseFormRegistrationProps<T extends BaseFields> = {
  identifier: string;
  submit: (values: T) => Promise<Question>;
  validate: () => Promise<boolean>;
  watch: UseFormWatch<T>;
  defaultValues: T;
  reset: UseFormReset<T>;
  formState: FormState<T>;
};

export const useFormRegistration = <T extends BaseFields>({
  identifier,
  submit,
  validate,
  reset,
  watch,
  formState,
  defaultValues,
}: UseFormRegistrationProps<T>) => {
  const { registerForm, unregisterForm, updateFormEntry, resetFailed } =
    useQuestionFormsOrchestrator();
  const [hasChanges, setHasChanges] = useState(false);

  const {
    submit: submitWrapper,
    validate: validateWrapper,
    values: valuesRef,
    savedValues: savedValuesRef,
  } = useStableFormValues({
    submit,
    validate,
    defaultValues,
  });
  const previousDefaultsRef = useRef(defaultValues);

  const handleReset = () => {
    setHasChanges(false);
    updateFormEntry(identifier, { hasChanges: false });
    resetFailed(identifier);
    reset(defaultValues);
  };

  useEffect(() => {
    const hasDraft = isUnsavedDraft(
      valuesRef.current,
      previousDefaultsRef.current,
      savedValuesRef.current,
    );
    previousDefaultsRef.current = defaultValues;
    reset(defaultValues, { keepValues: hasDraft });
  }, [defaultValues, reset, valuesRef, savedValuesRef]);

  useEffect(() => {
    const subscription = watch(() => {
      if (!isEmpty(formState?.errors)) validate();
    });

    return () => subscription.unsubscribe();
  }, [formState, validate, watch]);

  useEffect(() => {
    registerForm(identifier, {
      submit: submitWrapper,
      validate: validateWrapper,
      hasChanges: false,
    });
    return () => unregisterForm(identifier);
  }, [
    identifier,
    registerForm,
    unregisterForm,
    submitWrapper,
    validateWrapper,
  ]);

  useEffect(() => {
    const syncChanges = (value: unknown) => {
      valuesRef.current = value as T;
      const changed = !isEqual(value, defaultValues);
      setHasChanges(changed);
      updateFormEntry(identifier, { hasChanges: changed });
    };
    syncChanges(watch());
    const subscription = watch(syncChanges);
    return () => {
      setHasChanges(false);
      updateFormEntry(identifier, { hasChanges: false });
      subscription.unsubscribe();
    };
  }, [defaultValues, watch, identifier, updateFormEntry, valuesRef]);

  return { hasChanges, handleReset };
};

const useStableFormValues = <T extends BaseFields>({
  submit,
  validate,
  defaultValues,
}: Pick<
  UseFormRegistrationProps<T>,
  'submit' | 'validate' | 'defaultValues'
>) => {
  const valuesRef = useRef<T>(defaultValues);
  const savedValuesRef = useRef<T>();
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const submitWrapper = useCallback(async () => {
    const values = valuesRef.current;
    const saved = await submitRef.current(values);
    savedValuesRef.current = values;
    return saved;
  }, []);

  const validateWrapper = useCallback(() => validateRef.current(), []);

  return {
    submit: submitWrapper,
    validate: validateWrapper,
    values: valuesRef,
    savedValues: savedValuesRef,
  };
};
